"use client"

import { useState, useEffect } from "react"
import { createContainer } from "unstated-next"

import { createClientComponentClient } from "@supabase/auth-helpers-nextjs"
import { User, Session } from "@supabase/supabase-js"
import { useUserCountry } from "../user/useUserCountry"

import { authClient } from "@/lib/auth-client"
import { user } from "@/db/schema"
import type { InferSelectModel } from "drizzle-orm"
import { $getUser } from "./actions"

// Create type from your schema
type UserData = InferSelectModel<typeof user>

const UserContainer = createContainer(() => {
  const supabase = createClientComponentClient()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [sessionData, setSessionData] = useState<Session | null>(null)
  const [userData, setUserData] = useState<UserData | null>(null)
  const { countryCode } = useUserCountry()
  const [loginRequiredModalOpen, setLoginRequiredModalOpen] = useState(false)
  const authSession = authClient.useSession()

  const isProUser =
    userData && (userData.tokens > 0 || userData.unlimitedTokensEndDate)

  const sendVerificationEmail = async () => {
    try {
      if (!userData?.email) {
        throw new Error("No email address found")
      }

      const callbackUrl = `${process.env.NEXT_PUBLIC_APP_URL}/verify?token=${userData.id}`

      await authClient.sendVerificationEmail({
        email: userData.email,
        callbackURL: callbackUrl,
      })
      console.log("Email sending", callbackUrl, userData.email)

      return { success: true }
    } catch (err) {
      console.error("Error sending verification email:", err)
      throw err instanceof Error
        ? err
        : new Error("Failed to send verification email")
    }
  }
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSessionData(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchUserData()
      }
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSessionData(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        await fetchUserData()
      } else {
        setUserData(null)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    async function loadUser() {
      if (authSession?.data?.user?.id) {
        const userData = await $getUser({ email: authSession.data.user.email })
        if (userData) {
          setUserData(userData[0])
        } else {
          console.error("Error: No user data found")
        }
      }
      setLoading(false)
    }

    loadUser()
  }, [authSession, supabase])

  const fetchUserData = async () => {
    try {
      if (!authSession?.data?.user?.id) return

      const userData = await $getUser({ email: authSession.data.user.email })
      setUserData(userData[0])
    } catch (err) {
      console.error("Error fetching user data:", err)
    }
  }

  const registerUser = async (provider: "google" | "email") => {
    if (!user) return

    const refAffiliateId = localStorage.getItem("processedRef")
    console.log(refAffiliateId, "ref affiliate id pls be here")

    try {
      const { error } = await supabase.from("users").upsert({
        id: user.id,
        auth_provider: provider,
        registration_country: countryCode,
        email: user.email,
        created_at: new Date().toISOString(),
        free_creations: 0,
        tokens: 0,
        unlimited: false,
        ref_affiliate_id: refAffiliateId || null,
      })

      if (error) throw error

      await fetchUserData()
    } catch (err) {
      console.error("Registration error:", err)
      throw err
    }
  }

  const checkEmailVerificationStatus = async () => {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    return data.session?.user?.email_confirmed_at !== null
  }

  const signInWithEmail = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    setUser(data.user)
    await fetchUserData()
    return data
  }

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
    return data
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setUser(null)
    setUserData(null)
    setSessionData(null)
  }

  const isUserExists = async (email: string) => {
    const { data, error } = await supabase
      .from("users")
      .select("id")
      .eq("email", email)
      .single()

    if (error && error.code !== "PGRST116") {
      throw error
    }

    return !!data
  }

  return {
    signInWithEmail,

    signUp,
    signOut,
    registerUser,
    isUserExists,
    loading,
    user,
    sessionData,
    isProUser,
    userData,
    countryCode,
    loginRequiredModalOpen,
    setLoginRequiredModalOpen,
    fetchUserData,
    setUserData,
    authSession,
    sendVerificationEmail,
    checkEmailVerificationStatus,
  }
})

export const UserProvider = UserContainer.Provider
export const useUser = UserContainer.useContainer
